import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./RegisterStyle.css";

const Login = () => {
  // Form state management
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // UI state management
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showVerificationWarning, setShowVerificationWarning] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  // Form validation
  const validateForm = () => {
    if (!formData.email.trim()) {
      setError("Please enter your email address");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (!formData.password) {
      setError("Please enter your password");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await login(formData.email, formData.password);

      // Store remember me preference if needed
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
      }

      navigate("/dashboard");
    } catch (err) {
      // Check if error is due to unverified email
      if (err.response?.data?.code === "EMAIL_NOT_VERIFIED") {
        setShowVerificationWarning(true);
        setError(err.response.data.message);
        localStorage.setItem("pendingVerificationEmail", formData.email);
      } else {
        setShowVerificationWarning(false);
        setError(err.response?.data?.message || "Invalid email or password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container-main">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-9 col-xl-8">
            <div className="row g-0 login-card-shadow login-rounded-modern overflow-hidden login-fade-in">
              {/* Left Side - Login Form */}
              <div className="col-md-7 bg-white p-5">
                <div className="login-form-header mb-4">
                  <h3 className="fw-bold mb-2">
                    <i className="fa fa-sign-in login-header-icon me-2"></i>
                    Welcome Back
                  </h3>
                  <p className="text-muted login-header-subtitle">
                    Sign in to access your dashboard and manage conversions
                  </p>
                </div>

                {/* Error Alert */}
                {error && !showVerificationWarning && (
                  <div className="login-alert-danger mb-3">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    <span>{error}</span>
                  </div>
                )}

                {/* Email Verification Warning */}
                {showVerificationWarning && (
                  <div className="login-verification-warning mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <i className="fa fa-envelope-open text-warning me-2 mt-1"></i>
                      <div>
                        <strong className="d-block mb-1">Email Verification Required</strong>
                        <span>{error}</span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Link to="/verify-email" className="btn btn-sm login-btn-outline">
                        <i className="fa fa-paper-plane me-2"></i>
                        Resend Verification Email
                      </Link>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  {/* Email Field */}
                  <div className="mb-3">
                    <label className="login-form-label fw-semibold">
                      <i className="fa fa-envelope text-muted me-2"></i>
                      Email Address
                    </label>
                    <div className="login-input-group">
                      <input
                        type="email"
                        className="form-control login-input-modern"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="login-form-label fw-semibold mb-0">
                        <i className="fa fa-lock text-muted me-2"></i>
                        Password
                      </label>
                      <Link to="/forgot-password" className="login-link-small">
                        <i className="fa fa-question-circle me-1"></i>
                        Forgot password?
                      </Link>
                    </div>
                    <div className="login-input-group position-relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control login-input-modern"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                        required
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="login-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex="-1"
                      >
                        <i className={`fa fa-eye${showPassword ? "-slash" : ""}`}></i>
                      </button>
                    </div>
                  </div>

                  {/* Remember Me Checkbox */}
                  <div className="mb-4">
                    <div className="login-checkbox-wrapper">
                      <input
                        type="checkbox"
                        id="rememberMe"
                        className="login-checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <label htmlFor="rememberMe" className="login-checkbox-label">
                        <i className="fa fa-check login-checkbox-icon"></i>
                        <span>Remember me for 30 days</span>
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="btn login-btn-primary w-100 mb-3"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Signing In...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-sign-in me-2"></i>
                        Sign In to Dashboard
                      </>
                    )}
                  </button>

                  {/* Register Link */}
                  <div className="text-center login-register-link">
                    <p className="text-muted mb-0">
                      Don't have an account?{" "}
                      <Link to="/register" className="login-link fw-semibold">
                        Create one here
                        <i className="fa fa-arrow-right ms-1"></i>
                      </Link>
                    </p>
                  </div>
                </form>

                {/* Security Badge */}
                <div className="login-security-section mt-4 pt-3 border-top">
                  <div className="login-security-badge">
                    <i className="fa fa-shield me-2"></i>
                    <span>SSL Secured | Your data is encrypted and protected</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Branding & Features */}
              <div className="col-md-5 login-gradient-bg text-white p-5 d-flex flex-column justify-content-between">
                <div>
                  <div className="login-brand-section mb-4">
                    <i className="fa fa-file-pdf-o login-brand-icon mb-3"></i>
                    <h2 className="fw-bold mb-3 login-brand-title">PDF API Platform</h2>
                    <p className="login-brand-description mb-4">
                      Access your dashboard and manage all your document conversions with powerful tools.
                    </p>
                  </div>

                  {/* Features List */}
                  <div className="login-features-list">
                    <div className="login-feature-item mb-3">
                      <i className="fa fa-bolt fa-lg me-3"></i>
                      <span>Lightning-fast conversions</span>
                    </div>
                    <div className="login-feature-item mb-3">
                      <i className="fa fa-shield fa-lg me-3"></i>
                      <span>Enterprise-grade security</span>
                    </div>
                    <div className="login-feature-item mb-3">
                      <i className="fa fa-cloud fa-lg me-3"></i>
                      <span>Cloud-based infrastructure</span>
                    </div>
                    <div className="login-feature-item mb-3">
                      <i className="fa fa-line-chart fa-lg me-3"></i>
                      <span>Real-time analytics</span>
                    </div>
                    <div className="login-feature-item">
                      <i className="fa fa-support fa-lg me-3"></i>
                      <span>Excellent customer support</span>
                    </div>
                  </div>
                </div>

                {/* Stats Display */}
                <div className="login-stats-container mt-4">
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="login-stat-card">
                        <div className="login-stat-number">25K+</div>
                        <div className="login-stat-label">Active Users</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="login-stat-card">
                        <div className="login-stat-number">1M+</div>
                        <div className="login-stat-label">Conversions</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
